import Vue, { DirectiveOptions, VNodeDirective } from 'vue'

/**
 * Convert to kebab case
 */
const kebabCase = (str: string) => str.replace(/([a-zA-Z0-9])([A-Z])/g, '$1-$2').toLowerCase()

/**
 * Get a full list of BEM class names for the directive binding
 */
const getBEMClasses = (binding: VNodeDirective, context: Vue, old: boolean = false) => {
    const { arg, modifiers: modifierObject } = binding
    const dModifiers = old ? binding.oldValue : binding.value
    const block = kebabCase(context.$options.name!)
    const element = arg ? `__${arg}` : ''
    let modifiers = Object.keys(modifierObject || {})
    if (typeof dModifiers === 'string') {
        modifiers = [...modifiers, dModifiers]
    } else if (dModifiers && typeof dModifiers === 'object') {
        if (Array.isArray(dModifiers)) {
            modifiers = [...modifiers, ...dModifiers.filter(v => v)]
        } else {
            // add truthy object keys
            modifiers = [...modifiers, ...Object.keys(dModifiers).filter(key => dModifiers[key])]
        }
    }
    return [
        element ? `${block}${element}` : block,
        ...modifiers.map(modifier => `${block}${element}--${modifier}`)
    ]
}

/**
 * Directive to allow for BEM naming for element class names
 *
 * Syntax: v-bem:<element>[.<modifier>[...]][="<dynamic modifiers>..."]
 * Translates to CSS classes: <block>__<element>, <block>__<element>--<modifiers> (for each mod)
 *
 * Dynamic modifiers can be either a string, list of strings or truthy values' keys from an object
 *
 * Only usable in the scope of a single component (as intended)
 *
 * Example:
 *
 *     <div v-bem>
 *         <span v-bem:text>Text</span>
 *         <div v-bem:content.primary>
 *             <span v-bem:content-item>Item</span>
 *             <span v-bem:content-item="'secondary'">
 *                 Another
 *             </span>
 *         </div>
 *     </div>
 *     ...
 *     @Component({ name: 'test-component' })
 *
 * Renders as:
 *
 *     <div class="test-component">
 *         <span class="test-component__text">Text</span>
 *         <div class="test-component__content test-component__content--primary">
 *             <span class="test-component__content-item">Item</span>
 *             <span class="test-component__content-item test-component__content-item--secondary">
 *                 Item
 *             </span>
 *         </div>
 *     </div>
 *
 * @see http://getbem.com/introduction/
 */
const BEM: DirectiveOptions = {
    bind(el, binding, { context }, vnode) {
        // console.log('bind', context!.$options.name, getBEMClasses(binding, context!))
        el.classList.add(...getBEMClasses(binding, context!))
        // console.log('bind', context!.$options.name, vnode)
    },
    update(el, binding, { context }) {
        el.classList.remove(...getBEMClasses(binding, context!, true))
        el.classList.add(...getBEMClasses(binding, context!))
    }
}

export default BEM
